import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getMonthMatrix, isBeforeDay, isSameDay, parseYmd, toYmd } from '../utils/calendar';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function DatePickerModal({ open, title, value, min, max, onClose, onSelect }) {
  const selected = parseYmd(value);
  const minDate = parseYmd(min) || new Date();
  const maxDate = parseYmd(max);

  const initial = selected || minDate || new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
  }, [open, value, selected]);

  if (!open) return null;

  const cells = getMonthMatrix(viewYear, viewMonth);
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const goMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const pickDay = (date) => {
    if (!date) return;
    if (isBeforeDay(date, minDate)) return;
    if (maxDate && isBeforeDay(maxDate, date)) return;
    onSelect(toYmd(date));
    onClose();
  };

  return createPortal(
    <div className="sw-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="sw-modal sw-modal--date"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sw-date-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sw-modal__head">
          <h3 id="sw-date-modal-title">{title}</h3>
          <button type="button" className="sw-modal__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="sw-calendar">
          <div className="sw-calendar__nav">
            <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month">
              <ChevronLeft size={20} />
            </button>
            <span className="sw-calendar__month">{monthLabel}</span>
            <button type="button" onClick={() => goMonth(1)} aria-label="Next month">
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="sw-calendar__weekdays">
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="sw-calendar__grid">
            {cells.map((date, i) => {
              if (!date) {
                return <span key={`e-${i}`} className="sw-calendar__day sw-calendar__day--empty" />;
              }
              const disabled =
                isBeforeDay(date, minDate) || (maxDate && isBeforeDay(maxDate, date));
              const selectedDay = isSameDay(date, selected);
              const today = isSameDay(date, new Date());

              return (
                <button
                  key={toYmd(date)}
                  type="button"
                  className={`sw-calendar__day${selectedDay ? ' sw-calendar__day--selected' : ''}${today ? ' sw-calendar__day--today' : ''}`}
                  disabled={disabled}
                  onClick={() => pickDay(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        <footer className="sw-modal__foot">
          <button type="button" className="btn-outline sw-modal__btn" onClick={onClose}>
            Cancel
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
